/**
 * Nominal dimensions for every staging prop, in INCHES, as {x, y, z} =
 * width x height x depth — the same axis convention as `object.dimensions`.
 *
 * These are researched, not invented. `basis` records where each number came
 * from so a later reader can tell a product spec from a computed one:
 *   'spec'     a retailer or manufacturer spec page
 *   'standard' an industry standard size
 *   'derived'  computed from a cited figure; the arithmetic is in the comment
 *
 * THIS TABLE IS THE REGISTRY. `STAGING_DECOR_TYPES` is derived from its keys,
 * so registering a prop's dimensions is the whole job of adding a type — the
 * same contract dorm styling uses, where a type a planner can emit but the
 * ownership set omits would accumulate forever. Do not hand-list types
 * anywhere else.
 */

const STAGING_PROP_DIMS = {
  // Bath towel 27 x 52 flat. The hotel fold is three folds lengthwise then
  // thirds across the width: 27/3 x 52/3, about nine layers of ~0.24 in terry.
  'staging-towel-folded': { x: 9, y: 2.2, z: 17.3, basis: 'derived' },

  // Folded over a bar, hanging both sides. The BOX is the whole assembly, not
  // the towel: an 18 in bar (the standard length that holds one bath towel)
  // and a 3.4 in projection, which covers the 2.94-3.44 in Kohler and Moen
  // publish plus the two layers of cloth. The bar sits at 48 in AFF and is the
  // TOP of the box.
  //
  // The DROP is a staging choice, not a measurement. A 52 in towel folded once
  // over the rod drops 26 in and dangles to 22 in off the floor — what a used
  // bathroom looks like. Staged and hotel bathrooms fold the length down first,
  // so the towel reads as a short thick block on the rod: 52 folded to 26, then
  // halved over the bar, is 13 in. That also lifts the towel's visual centre
  // from 35 in — counter height, which made the rod look like it was AT the
  // counter — to 41 in, clear above it.
  'staging-towel-hanging': { x: 18, y: 13, z: 3.4, basis: 'derived' },

  // The SAME fitting at bath-towel size, for a room that already has a hand
  // towel on its ring. The hanging towel above is sized as a HAND towel on
  // purpose — when it is the room's only towel it does that job, mounted over
  // the counter. Once the ring is there, the bar is free to be what a bar
  // beside a tub actually holds.
  //
  // A 24 in bar is the standard length for one bath towel (18 / 24 / 30 are
  // the stocked sizes; 18 is the hand-towel bar). The DROP is the 52 in towel
  // folded ONCE over the rod — 26 in — rather than folded down first, because
  // here nothing needs the towel kept clear of a counter and the length is
  // the whole point: 24 wide against 26 tall reads as a bath towel, where the
  // hand towel's 18 against 13 reads as a hand towel.
  //
  // The renderer is shared with `staging-towel-hanging` and sizes everything
  // from these numbers, so the cloth comes out 18 x 23.4 in.
  'staging-towel-bath': { x: 24, y: 26, z: 3.4, basis: 'spec + derived' },

  // A hand towel on a RING beside the basin — the commonest hand-towel fitting
  // there is, and more standard than a bar for that spot.
  //
  // The box spans the whole assembly: a 7 in ring (the usual 6-8 in) with a
  // hand towel folded lengthwise draped through it. Ring top at 50 in AFF and
  // a ~13 in drape from its lower bar put the bottom near 32 in, so the box is
  // 18 in tall. Projection matches the bar's, which covers the 2.94-3.44 in
  // Kohler and Moen publish plus two layers of cloth.
  'staging-towel-ring': { x: 8, y: 18, z: 3.4, basis: 'spec + derived' },

  // A roll's LENGTH is the folded width it was rolled from; its DIAMETER comes
  // from the 54 in being rolled, so 54 x 0.24 in of terry packs to ~4.5 dia
  // either way. A bath towel folded in HALF (27 -> 13.5) is what goes in a
  // floor basket; the thirds fold, giving a 9 in roll, is for a shelf or a
  // tray. No published source states a rolled diameter.
  'staging-towel-rolled': { x: 4.5, y: 13.5, z: 4.5, basis: 'derived' },

  // Sized to the towels it holds rather than picked off a shelf, and sized to
  // look FULL: four 4.5 in rolls in a square need about 10.9 in of inner
  // diameter, which 12 gives snugly. The literature puts 3 or 4 rolls in a
  // basket, so a wider one is not better — at 14 in the same rolls leave a
  // ring of empty space and the basket reads under-filled. 12 is the bottom of
  // the 11.75-18 in retail range for a bathroom floor basket; the 10.25 in one
  // this started as is a countertop size and read as a bucket beside a bath.
  // The box height is the ROLL, not the basket — the tops standing proud of
  // the rim is the whole point, so the basket is drawn shallower.
  'staging-towel-basket': { x: 12, y: 13.5, z: 12, basis: 'spec + derived' },

  // 12 x 6 is the commonest retail vanity tray and was the original pick, but
  // it is too big for the counter that is actually there: with the basin
  // excluded, a 12 in tray fits only 15 of 47 corpus vanities. 9 in fits 20,
  // and 9 is the FLOOR for a tray that still does its job — its interior is
  // then 7.92 in, and a candle (3) plus a gap (1.5) plus a jar (3.15) needs
  // 7.65. An 8 in tray reaches 23 vanities but holds one item, which trades
  // one nice touch for two.
  'staging-tray': { x: 9, y: 1.5, z: 6, basis: 'spec + derived' },
  // A bar of soap ON ITS DISH, as a hotel leaves it — one object, because a
  // dish holds exactly one soap and nothing else, so a separate host would buy
  // nothing but registry work.
  //
  // TURNED: the bar runs front-to-back, so the box presents 3.25 in to the
  // counter run rather than the dish's 5 in length. That matters because the
  // counter's constraint is 1-D — props compete for LENGTH along the run while
  // ~21 in of depth sits unused. Measured over the 47 corpus bathrooms that
  // have a surface host: turned, the dish seats on 32 of them, as many as the
  // bare bar it replaces; laid lengthwise it would have seated 25, so the dish
  // would have cost 7 rooms their soap.
  'staging-soap': { x: 3.25, y: 1.75, z: 5, basis: 'spec + derived' },

  // A cluster of 2-3 amenity bottles — the dispenser-and-lotion group a hotel
  // leaves by the basin and in the shower niche. ONE object: they are always
  // a group, never placed or moved singly.
  //
  // DEPTH is the binding dimension, not a style choice, and the repo holds two
  // niche defaults: the pipeline builds them 4.02 in deep
  // (`designAutomation/rules/showerRules.js`) and the procedural gallery
  // declares 3.5 in. At 3 in deep the cluster cleared the first by 0.4 mm and
  // failed the second outright, so it is 2.5 in and fits both with margin.
  // 8 in tall clears a 12 in niche opening with room to spare; a tall pump
  // bottle is 7-9 in.
  'staging-bottles': { x: 4, y: 8, z: 2.5, basis: 'derived' },

  // An 8 oz jar candle: 2.5-3.5 dia, 3.3-4.0 tall.
  'staging-candle': { x: 3, y: 3.8, z: 3, basis: 'spec' },

  // Apothecary jar, body 3.15 x 3.94, about 4.5 overall with the lid.
  'staging-jar': { x: 3.15, y: 4.5, z: 3.15, basis: 'spec' },

  // Three volumes at a 10 x 8 coffee-table trim. Designers keep a stack under 8.
  'staging-book-stack': { x: 10, y: 3.6, z: 8, basis: 'standard' },

  // Florists put the arrangement at 1.5x the vase height above the rim, so a
  // 6 in vessel finishes near 15.
  'staging-stems': { x: 4, y: 15, z: 4, basis: 'standard' },

  'staging-bowl': { x: 8, y: 3, z: 8, basis: 'spec' },

  // A "6 inch pot" is the DIAMETER, not the height: 6.2 x 6.2, plus ~8 of plant.
  'staging-plant-small': { x: 6.2, y: 14, z: 6.2, basis: 'derived' },

  // An 18 x 24 print — the standard poster size — plus ~1.5 in of moulding a
  // side. Art runs two-thirds the width of the furniture below it, and 0.66 x a
  // 36 in vanity (the most common single width) is 24 in.
  'staging-art': { x: 21, y: 27, z: 1.25, basis: 'spec' },

  // A wall-mounted toilet roll holder, as ONE object: the bracket and the roll
  // on it, because a holder is never staged empty and the roll is the half you
  // can see.
  //
  // A standard roll is 4-4.5 in wide and 4-5 in in outer diameter, so the box
  // takes the top of both. WIDTH is the roll plus a post either side; DEPTH is
  // the published "roll diameter + 1 in" sizing rule, which also lands inside
  // the 4-8 in arm projection holders are made in. Mounted by its CENTRE at
  // the NKBA's 26 in — unlike the towels, whose number describes the hardware
  // at the top of the box.
  //
  // HEIGHT is the 4.5 in roll plus 2 in for the loose tail hanging off the
  // front of it, which is the detail that reads as ready rather than fitted.
  // At the roll's own 4.5 in the tail has nowhere to go and hangs out of the
  // bottom of the box — caught by `rendererBboxContainment`, which derives its
  // cases from this table.
  'staging-toilet-paper': { x: 6, y: 6.5, z: 5, basis: 'spec + derived' },

  // A PAIR of single-point hooks in a row with a BODY towel over each — one
  // object, because two hooks 10 in apart are a fitting, not two fittings, and
  // are never placed or moved singly. Two and not four: the staging
  // literature's whole warning is too many, and a row of four reads as a
  // changing room.
  //
  // 12 in between centres — the top of the 8-12 in spacing recommended so
  // towels can dry (4-6 in is for the two hooks of a DOUBLE, which is a
  // different fitting). It was 10 with a 13 in towel, and the pair overlapped
  // by three inches and merged into one shape; the reference pair hangs with
  // about an inch between them. The hook is the TOP of the box at 66 in AFF, the
  // widely-accepted height within the 60-70 in band hooks are mounted in.
  //
  // The DROP is 26 in: a 27 x 52 towel folded once over the hook, which is the
  // same derivation `staging-towel-bath` uses for a bar and the figure the
  // literature gives for a towel on a hook. It was 13 in — the hand towel's
  // staged fold — carried over from when the hooks were a generic third
  // fitting rather than the body-towel alternative to the bar, and at that
  // length the towels read as stubs rather than as something you would dry
  // off with. At 66 in AFF a 26 in drop finishes at 40 in, clear of a 35 in
  // counter by more than the drape clearance.
  //
  // WIDTH is the 12 in between centres plus one towel's hanging width. That
  // width is 11 in: a towel pinched through a hook fans back out within a few
  // inches of it and hangs a little under the 13.5 in it measures folded in
  // half, roughly half as wide as it is long. The first figure here was 9 in,
  // on the guess that the cloth stays bunched; rendered beside a reference
  // photograph it read as a wedge a third as wide as it was tall.
  //
  // DEPTH is 6 in and NOT the bar's 3.4: a towel over a hook is a BUNCH, not
  // a sheet. The hook tip stands 2-3 in off the wall and the cloth wraps round
  // it, so the gathered top is ~5 in deep — the back layer against the wall,
  // the front bulging past the hook — and only the hem flattens to the two
  // layers a bar's towel is all the way down. At 3.4 in the drape had to be
  // squashed to a plate to fit, and from the side that is exactly what it
  // looked like.
  'staging-towel-hooks': { x: 23, y: 28, z: 6, basis: 'spec + derived' },

  // A pump bottle on the counter — the LOTION that pairs with the bar of soap,
  // which is how a hotel leaves a basin. Not a replacement for the soap: both,
  // as an amenity pair. `staging-bottles` is the shower-niche cluster and stays
  // niche-only.
  //
  // An 8 oz dispenser is 2.25-2.4 in across and 5.5-7.1 in tall depending on
  // the pump; the tall end is a foaming pump, which adds about an inch. Taking
  // the SHORT end deliberately: this stands beside a 1.75 in soap dish and a
  // 4.5 in jar, and a 7 in bottle out-tops the 4.5 in apothecary jar and the
  // 3.8 in candle enough to read as a cleaning product rather than part of the
  // vignette.
  'staging-pump-bottle': { x: 2.4, y: 5.5, z: 2.4, basis: 'spec' },

  // ---- Kitchen (2026-10-18). Each box is the MEASURED extent of the reviewed
  // model, the largest over the three styles (a style changes what the prop is
  // — lemons or pears, an oak or a stone tray — so the box must hold every
  // one). The renderer seats its model by that extent: see
  // `stagingKitchen/kit.js groupToDescriptors`.
  //
  // An 11 in glazed bowl heaped with eleven fruit; standing pears are the
  // tallest pile at 9.95 in.
  'staging-fruit-bowl': { x: 11, y: 10, z: 11, basis: 'derived' },
  // Seven tulips in an 8.3 in stoneware vase; heads spread to 12.2 in.
  'staging-vase-tulips': { x: 11.5, y: 22.2, z: 12.2, basis: 'derived' },
  // An amber Boston-round pump bottle and a dish brush on a tray: 10 x 5.5 in
  // oak, 8.5 in round stone (the brush handle overhangs to 9.2 in deep) or a
  // 10 x 6 in walnut oval.
  'staging-dish-soap-set': { x: 10.25, y: 7.5, z: 9.2, basis: 'derived' },
  // A walnut paddle board shortened to ~15.5 in so it fits under an upper
  // cabinet (the corpus median counter-to-upper gap is 18 in), and an 11 in
  // round board leaned in front of it. Depth is the lean off the backsplash.
  'staging-cutting-boards': { x: 16.6, y: 15.6, z: 4.75, basis: 'derived' },
  // A 5.6 in crock with four 9.5-11.5 in tools, ~15 in tall overall.
  'staging-utensil-crock': { x: 6.3, y: 15.1, z: 6.8, basis: 'derived' },
  // Folded in thirds and draped over an oven handle; the box and where the
  // handle sits in it come from `teaTowel.js`.
  'staging-tea-towel': (() => {
    const b = require('./teaTowel').teaTowelBox();
    return {
      x: b.x,
      y: Math.ceil(b.y * 20) / 20,
      z: Math.ceil(b.z * 20) / 20,
      basis: 'derived',
    };
  })(),
  // The fallback when the range is a catalog model: a tea towel folded to
  // 8 x 5.5 in on the counter, two layers.
  'staging-dish-towel-folded': { x: 8, y: 1.2, z: 5.5, basis: 'derived' },
  // Basil in a 5.2 in terracotta pot, leaves to 11.5 in.
  'staging-herb-pot': { x: 6.7, y: 11.5, z: 6.45, basis: 'derived' },
  // An 11 x 14 in framed print (0.9 in deep) tipped back 12 degrees onto the
  // backsplash: 14 cos + 0.9 sin tall, 14 sin + 0.9 cos deep.
  'staging-art-leaned': { x: 11, y: 13.9, z: 3.8, basis: 'derived' },
  // An open cookbook on a 10 x 11.5 in wooden stand tipped back 18 degrees.
  'staging-cookbook-stand': { x: 10, y: 11.1, z: 5.5, basis: 'derived' },
  // A matched pair of 8.4 in turned mills, or 7.5 in acrylic grinders.
  'staging-mills': { x: 6.1, y: 8.45, z: 3.4, basis: 'derived' },
  // Three 7 in glass jars of one height, 4.6 / 4.0 / 3.4 in across, lids on,
  // in ONE ROW against the backsplash with 0.4 in between lids.
  'staging-canisters': { x: 13.1, y: 8.5, z: 4.7, basis: 'derived' },
};

// A prop at OTHER than its catalogue size. The hooks are a pair in a 23 in
// box; where no wall has 31 in clear for the pair, ONE hook with one towel
// still goes up ("they don't have to spawn both hooks. can just spawn one",
// 2026-10-16). Same type, same height, the towel's own 11 in width; the
// renderer draws one hook or two by the width it is handed.
const STAGING_PROP_VARIANT_DIMS = {
  'staging-towel-hooks': {
    single: { x: 11, y: 28, z: 6 },
  },
};

const INCHES_TO_M = 0.0254;

/** Nominal dimensions in METRES, the unit every object record uses. */
/**
 * A prop's box in metres — its catalogue box, or a named variant's
 * (`stagingPropDimsM('staging-towel-hooks', 'single')`). Null for a type or
 * a variant that does not exist, never a fallback to the other size.
 */
function stagingPropDimsM(type, variant = null) {
  const d = variant
    ? STAGING_PROP_VARIANT_DIMS[type]?.[variant]
    : STAGING_PROP_DIMS[type];
  if (!d) return null;
  return { x: d.x * INCHES_TO_M, y: d.y * INCHES_TO_M, z: d.z * INCHES_TO_M };
}

module.exports = {
  STAGING_PROP_DIMS,
  STAGING_PROP_VARIANT_DIMS,
  stagingPropDimsM,
  INCHES_TO_M,
};
